import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const roles = [
  { v: "citizen", label: "Citizen — I have ideas" },
  { v: "creator", label: "Content Creator (Academic / In-field)" },
  { v: "scientist", label: "Scientist / Researcher" },
  { v: "company", label: "Company Representative" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", display_name: "", role: "citizen", credentials: "" });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      localStorage.setItem("eco_show_welcome", "1");
      toast.success(form.role === "citizen" ? "Welcome to the chamber" : "Verified account created");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const needsCreds = form.role === "creator" || form.role === "scientist";

  return (
    <div className="mx-auto max-w-md px-6 py-24">
      <div className="mono text-xs text-primary mb-2">// join</div>
      <h1 className="text-4xl font-black tracking-tighter mb-8">Enter the chamber.</h1>
      <form onSubmit={submit} className="space-y-5 border border-border p-8">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Display name</Label>
          <Input data-testid="reg-name" required value={form.display_name} onChange={upd("display_name")} className="mt-2 rounded-none bg-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
          <Input data-testid="reg-email" type="email" required value={form.email} onChange={upd("email")} className="mt-2 rounded-none bg-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Password (6+ chars)</Label>
          <Input data-testid="reg-password" type="password" required value={form.password} onChange={upd("password")} className="mt-2 rounded-none bg-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">I am a…</Label>
          <Select value={form.role} onValueChange={upd("role")}>
            <SelectTrigger data-testid="reg-role" className="mt-2 rounded-none bg-input"><SelectValue /></SelectTrigger>
            <SelectContent>{roles.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {needsCreds && (
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Credentials / Institution</Label>
            <Textarea data-testid="reg-credentials" placeholder="e.g. Postdoc, MIT Media Lab; Materials Sci PhD; 12 yrs field ecology" value={form.credentials} onChange={upd("credentials")} className="mt-2 rounded-none bg-input" />
          </div>
        )}
        <Button type="submit" disabled={busy} data-testid="reg-submit" className="w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
          {busy ? "Creating…" : "Create account"}
        </Button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground">Already inside? <Link to="/login" className="text-primary hover:underline">Sign in →</Link></p>
    </div>
  );
}
